import { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import SlideOverPanel from "../components/SlideOverPanel";
import {
  Phone, Mail, ArrowRight, Users, FileText, Clock, Check, Calendar,
  ChevronLeft, ChevronRight, MapPin, Loader2, RotateCcw, SquareCheck as CheckSquare,
} from "lucide-react";

const ICONS = { Call: Phone, Email: Mail, "Follow-up": ArrowRight, Meeting: Users, "Proposal Review": FileText, Task: CheckSquare, Other: Clock };
const ICON_COLORS = {
  Call: "text-green-400 bg-green-400/10",
  Email: "text-cyan-400 bg-cyan-400/10",
  "Follow-up": "text-amber-400 bg-amber-400/10",
  Meeting: "text-purple-400 bg-purple-400/10",
  "Proposal Review": "text-pink-400 bg-pink-400/10",
  Task: "text-blue-400 bg-blue-400/10",
  Other: "text-white/40 bg-white/5",
};
const PRIORITY_BADGE = {
  low: "bg-white/[0.06] text-white/30",
  medium: "bg-blue-500/15 text-blue-400",
  high: "bg-orange-500/15 text-orange-400",
  urgent: "bg-red-500/15 text-red-400",
};
const PRIORITY_LABEL = { low: "Low", medium: "Med", high: "High", urgent: "Urgent" };

function toYMD(d) { return d.toISOString().slice(0, 10); }
function addDays(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }
function startOfWeek(d) { const r = new Date(d); r.setDate(r.getDate() - ((r.getDay() + 6) % 7)); return r; }
function fmtDate(s) { return new Date(s + "T00:00").toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "short" }); }
function fmtMonth(d) { return d.toLocaleDateString("de-DE", { month: "long", year: "numeric" }); }
function fmtDateShort(s) { return new Date(s + "T00:00").toLocaleDateString("de-DE", { day: "2-digit", month: "short" }); }

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// CalItem — rich event card
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function CalItem({ item, onDone, onReopen, onOpen, onReschedule, showDate }) {
  const Icon = ICONS[item.action_type] || Clock;
  const iconColor = ICON_COLORS[item.action_type] || ICON_COLORS.Other;
  const isDone = item.status === "done";
  const isTask = item.type === "task";
  const [rescheduling, setRescheduling] = useState(false);
  const [newDate, setNewDate] = useState(item.date);

  return (
    <div
      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all group cursor-pointer ${isDone ? "opacity-40 border-white/[0.02] bg-white/[0.01]" : "border-white/[0.04] bg-white/[0.015] hover:bg-white/[0.04] hover:border-white/[0.08] active:scale-[0.995]"}`}
      data-testid={`cal-item-${item.id}`}
      onClick={(e) => { if (e.target.closest("button") || e.target.closest("input")) return; onOpen(item.deal_id); }}
    >
      {/* Toggle */}
      <button
        onClick={(e) => { e.stopPropagation(); isDone ? onReopen(item.id) : onDone(item.id); }}
        className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors ${isDone ? "bg-[#C9A96E]/10" : iconColor}`}
        data-testid={`cal-toggle-${item.id}`}
      >
        {isDone ? <Check className="w-4 h-4 text-[#C9A96E]" /> : <Icon className="w-4 h-4" />}
      </button>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-sm font-medium leading-tight ${isDone ? "line-through text-white/30" : "text-white/90"}`}>
            {item.deal_title}
          </span>
          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md uppercase tracking-wider ${PRIORITY_BADGE[item.deal_priority] || PRIORITY_BADGE.medium}`}>
            {PRIORITY_LABEL[item.deal_priority] || "Med"}
          </span>
          {isTask && <span className="text-[9px] px-1.5 py-0.5 rounded-md bg-blue-500/10 text-blue-400 uppercase tracking-wider font-bold">Task</span>}
        </div>
        <div className="flex items-center gap-2 mt-0.5 text-xs flex-wrap">
          <span className={`font-medium ${isDone ? "text-white/15" : "text-white/40"}`}>{item.action_type}</span>
          {item.note && <span className={`${isDone ? "text-white/10" : "text-white/25"}`}>— {item.note}</span>}
        </div>
        <div className="flex items-center gap-3 mt-1 flex-wrap">
          {item.primary_contact && <span className="text-white/25 text-[11px] flex items-center gap-1"><Users className="w-3 h-3" />{item.primary_contact}</span>}
          {item.location && <span className="text-white/20 text-[11px] flex items-center gap-1"><MapPin className="w-3 h-3" />{item.location}</span>}
        </div>
      </div>

      {/* Right side */}
      <div className="flex items-center gap-2 shrink-0">
        {showDate && <span className="text-white/20 text-[11px] font-mono hidden sm:block">{fmtDateShort(item.date)}</span>}
        {item.time && <span className="text-white/30 text-xs font-mono font-medium">{item.time}</span>}
        <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          {!isDone && (
            <button onClick={(e) => { e.stopPropagation(); setRescheduling(!rescheduling); }} className="p-1.5 text-white/15 hover:text-white/50 hover:bg-white/[0.05] rounded-lg" title="Reschedule" data-testid={`cal-reschedule-${item.id}`}>
              <Calendar className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Reschedule inline */}
      {rescheduling && (
        <div className="flex items-center gap-1.5 ml-2" onClick={e => e.stopPropagation()}>
          <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} className="bg-white/5 border border-white/10 text-white text-[11px] rounded-lg px-2 py-1.5 outline-none" />
          <button onClick={() => { onReschedule(item.id, newDate); setRescheduling(false); }} className="px-2.5 py-1.5 bg-[#C9A96E]/20 text-[#C9A96E] text-[11px] rounded-lg font-medium hover:bg-[#C9A96E]/30">OK</button>
        </div>
      )}
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Section Header
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function Section({ label, count, color, children }) {
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2.5 mb-2 px-1">
        <span className={`text-[11px] font-bold uppercase tracking-wider ${color}`}>{label}</span>
        <span className="text-white/15 text-[11px] font-mono bg-white/[0.03] px-1.5 py-0.5 rounded-md">{count}</span>
      </div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Month Grid — compact event rows
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function MonthGrid({ items, date, onDayClick, onItemClick }) {
  const year = date.getFullYear();
  const month = date.getMonth();
  const first = new Date(year, month, 1);
  const startDay = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = toYMD(new Date());
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div>
      <div className="grid grid-cols-7 gap-1.5 mb-1">
        {["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"].map(d => (
          <div key={d} className="text-center text-white/25 text-[11px] font-bold uppercase tracking-wider py-2">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {cells.map((d, i) => {
          if (!d) return <div key={`e-${i}`} className="min-h-[90px]" />;
          const ds = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
          const dayItems = items.filter(it => it.date === ds);
          const openItems = dayItems.filter(it => it.status === "open");
          const isToday = ds === today;
          const isPast = ds < today;
          const hasOverdue = isPast && openItems.length > 0;

          return (
            <div
              key={ds}
              className={`rounded-xl border p-1.5 min-h-[90px] transition-all cursor-pointer ${
                isToday
                  ? "bg-[#C9A96E]/[0.06] border-[#C9A96E]/25 shadow-sm shadow-[#C9A96E]/5"
                  : hasOverdue
                  ? "bg-red-500/[0.04] border-red-500/10"
                  : dayItems.length > 0
                  ? "bg-white/[0.015] border-white/[0.05] hover:bg-white/[0.03]"
                  : "border-white/[0.02] hover:border-white/[0.04]"
              }`}
              onClick={() => onDayClick(new Date(ds + "T00:00"))}
              data-testid={`month-day-${ds}`}
            >
              <div className="flex items-center justify-between mb-1 px-0.5">
                <span className={`text-[11px] font-bold ${isToday ? "text-[#C9A96E]" : hasOverdue ? "text-red-400/80" : "text-white/35"}`}>{d}</span>
                {dayItems.length > 0 && <span className="text-[9px] text-white/15 font-mono">{dayItems.length}</span>}
              </div>
              <div className="space-y-0.5">
                {dayItems.slice(0, 3).map(it => {
                  const Icon = ICONS[it.action_type] || Clock;
                  const isDone = it.status === "done";
                  return (
                    <button
                      key={it.id}
                      onClick={(e) => { e.stopPropagation(); onItemClick(it.deal_id); }}
                      className={`w-full flex items-center gap-1 px-1 py-0.5 rounded transition-colors text-left ${isDone ? "opacity-30" : "hover:bg-white/[0.05]"}`}
                      data-testid={`month-item-${it.id}`}
                    >
                      <Icon className={`w-2.5 h-2.5 shrink-0 ${isDone ? "text-white/15" : (ICON_COLORS[it.action_type] || "").split(" ")[0] || "text-white/30"}`} />
                      <span className={`text-[9px] truncate flex-1 ${isDone ? "text-white/15 line-through" : "text-white/50"}`}>{it.deal_title}</span>
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${isDone ? "bg-white/10" : (PRIORITY_BADGE[it.deal_priority] || "").split(" ")[0] || "bg-white/10"}`} />
                    </button>
                  );
                })}
                {dayItems.length > 3 && (
                  <span className="text-[8px] text-white/15 font-mono px-1 block">+{dayItems.length - 3} mehr</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Week Item — compact row for week grid
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function WeekItem({ item, onDone, onReopen, onOpen }) {
  const Icon = ICONS[item.action_type] || Clock;
  const isDone = item.status === "done";
  return (
    <div
      className={`flex items-center gap-1.5 px-1.5 py-1 rounded-lg transition-colors cursor-pointer ${isDone ? "opacity-30" : "hover:bg-white/[0.05] active:bg-white/[0.08]"}`}
      onClick={() => onOpen(item.deal_id)}
      data-testid={`week-item-${item.id}`}
    >
      <button onClick={(e) => { e.stopPropagation(); isDone ? onReopen(item.id) : onDone(item.id); }} className="shrink-0">
        {isDone ? <Check className="w-3 h-3 text-[#C9A96E]" /> : <Icon className={`w-3 h-3 ${(ICON_COLORS[item.action_type] || "").split(" ")[0] || "text-white/30"}`} />}
      </button>
      <div className="flex-1 min-w-0">
        <span className={`text-[11px] font-medium block truncate ${isDone ? "line-through text-white/20" : "text-white/70"}`}>{item.deal_title}</span>
        {item.primary_contact && <span className="text-[9px] text-white/20 block truncate">{item.primary_contact}</span>}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {item.time && <span className="text-white/20 text-[9px] font-mono">{item.time}</span>}
        <span className={`w-1.5 h-1.5 rounded-full ${(PRIORITY_BADGE[item.deal_priority] || "").split(" ")[0] || "bg-white/10"}`} />
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Main Page
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export default function TodayPage() {
  const { token } = useAuth();
  const [view, setView] = useState("agenda");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [loadingDeal, setLoadingDeal] = useState(false);

  const h = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const today = useMemo(() => toYMD(new Date()), []);

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const start = toYMD(addDays(new Date(), -90));
      const end = toYMD(addDays(new Date(), 90));
      const r = await axios.get(`${API}/calendar?start=${start}&end=${end}`, { headers: h });
      setItems(r.data);
    } catch { toast.error("Laden fehlgeschlagen"); }
    finally { setLoading(false); }
  };

  const markDone = async (id) => { try { await axios.put(`${API}/calendar/${id}/done`, {}, { headers: h }); setItems(p => p.map(i => i.id === id ? { ...i, status: "done" } : i)); } catch { toast.error("Fehler"); } };
  const reopen = async (id) => { try { await axios.put(`${API}/calendar/${id}/reopen`, {}, { headers: h }); setItems(p => p.map(i => i.id === id ? { ...i, status: "open" } : i)); } catch { toast.error("Fehler"); } };
  const reschedule = async (id, date) => { try { await axios.put(`${API}/calendar/${id}/reschedule?date=${date}`, {}, { headers: h }); setItems(p => p.map(i => i.id === id ? { ...i, date } : i)); toast.success("Verschoben"); } catch { toast.error("Fehler"); } };

  const openDeal = useCallback(async (dealId) => {
    setLoadingDeal(true);
    try {
      const r = await axios.get(`${API}/deals/${dealId}`, { headers: h });
      setSelectedDeal(r.data);
    } catch { toast.error("Deal nicht gefunden"); }
    finally { setLoadingDeal(false); }
  }, [h]);

  const handleDealUpdate = useCallback(async () => {
    if (selectedDeal) {
      try {
        const r = await axios.get(`${API}/deals/${selectedDeal.id}`, { headers: h });
        setSelectedDeal(r.data);
      } catch {}
    }
    fetchItems();
  }, [selectedDeal, h]);

  // Computed data
  const overdue = items.filter(i => i.date < today && i.status === "open");
  const dueToday = items.filter(i => i.date === today);
  const upcoming = items.filter(i => i.date > today && i.date <= toYMD(addDays(new Date(), 7)) && i.status === "open");
  const dayItems = items.filter(i => i.date === toYMD(selectedDate));
  const weekStart = startOfWeek(selectedDate);
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const viewBtns = [
    { key: "agenda", label: "Agenda" },
    { key: "day", label: "Tag" },
    { key: "week", label: "Woche" },
    { key: "month", label: "Monat" },
  ];

  if (loading) return <div className="p-8 flex justify-center items-center min-h-[50vh]"><Loader2 className="w-8 h-8 animate-spin text-[#C9A96E]" /></div>;

  return (
    <div className="p-4 sm:p-5 lg:p-6" data-testid="today-page">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl text-white tracking-tight" data-testid="today-title">Today</h1>
          <p className="text-white/35 text-sm mt-1">
            {overdue.length > 0 && <span className="text-red-400 font-semibold">{overdue.length} überfällig · </span>}
            {dueToday.filter(i => i.status === "open").length} heute fällig · {upcoming.length} bevorstehend
          </p>
        </div>
        <div className="flex items-center gap-1 bg-white/[0.03] rounded-xl p-1 border border-white/[0.05]" data-testid="view-switcher">
          {viewBtns.map(v => (
            <button
              key={v.key}
              onClick={() => setView(v.key)}
              className={`px-3.5 py-2 rounded-lg text-xs font-semibold transition-all ${view === v.key ? "bg-[#C9A96E]/15 text-[#C9A96E] shadow-sm shadow-[#C9A96E]/5" : "text-white/25 hover:text-white/50 active:text-white/60"}`}
              data-testid={`view-${v.key}`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Date Navigation ── */}
      {view !== "agenda" && (
        <div className="flex items-center gap-3 mb-5">
          <button
            onClick={() => setSelectedDate(d => addDays(d, view === "month" ? -30 : view === "week" ? -7 : -1))}
            className="p-2 text-white/25 hover:text-white/60 hover:bg-white/[0.04] rounded-lg transition-colors active:scale-95"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => setSelectedDate(new Date())}
            className="px-3 py-1.5 text-xs text-white/40 hover:text-white/70 bg-white/[0.03] rounded-lg font-medium transition-colors"
          >
            Heute
          </button>
          <span className="text-white text-sm font-semibold">
            {view === "day" && fmtDate(toYMD(selectedDate))}
            {view === "week" && `${fmtDate(toYMD(weekStart))} — ${fmtDate(toYMD(addDays(weekStart, 6)))}`}
            {view === "month" && fmtMonth(selectedDate)}
          </span>
          <button
            onClick={() => setSelectedDate(d => addDays(d, view === "month" ? 30 : view === "week" ? 7 : 1))}
            className="p-2 text-white/25 hover:text-white/60 hover:bg-white/[0.04] rounded-lg transition-colors active:scale-95"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* ═══ AGENDA VIEW ═══ */}
      {view === "agenda" && (
        <div data-testid="agenda-view" className="max-w-4xl">
          {overdue.length > 0 && (
            <Section label="Überfällig" count={overdue.length} color="text-red-400">
              {overdue.map(i => <CalItem key={i.id} item={i} onDone={markDone} onReopen={reopen} onOpen={openDeal} onReschedule={reschedule} showDate />)}
            </Section>
          )}
          <Section label="Heute" count={dueToday.filter(i => i.status === "open").length} color="text-[#C9A96E]">
            {dueToday.length === 0
              ? <p className="text-white/15 text-sm px-3 py-6">Keine Einträge für heute</p>
              : dueToday.map(i => <CalItem key={i.id} item={i} onDone={markDone} onReopen={reopen} onOpen={openDeal} onReschedule={reschedule} />)
            }
          </Section>
          {upcoming.length > 0 && (
            <Section label="Nächste 7 Tage" count={upcoming.length} color="text-white/30">
              {upcoming.map(i => <CalItem key={i.id} item={i} onDone={markDone} onReopen={reopen} onOpen={openDeal} onReschedule={reschedule} showDate />)}
            </Section>
          )}
          {overdue.length === 0 && dueToday.length === 0 && upcoming.length === 0 && (
            <div className="text-center py-16">
              <Clock className="w-12 h-12 text-white/[0.06] mx-auto mb-4" />
              <p className="text-white/20 text-sm">Keine bevorstehenden Aktionen oder Aufgaben</p>
            </div>
          )}
        </div>
      )}

      {/* ═══ DAY VIEW ═══ */}
      {view === "day" && (
        <div data-testid="day-view" className="max-w-4xl">
          {dayItems.length === 0
            ? <div className="text-center py-16"><p className="text-white/20 text-sm">Keine Einträge für {fmtDate(toYMD(selectedDate))}</p></div>
            : <div className="space-y-1.5">{dayItems.map(i => <CalItem key={i.id} item={i} onDone={markDone} onReopen={reopen} onOpen={openDeal} onReschedule={reschedule} />)}</div>
          }
        </div>
      )}

      {/* ═══ WEEK VIEW ═══ */}
      {view === "week" && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="week-view">
          {weekDays.map(d => {
            const ds = toYMD(d);
            const di = items.filter(i => i.date === ds);
            const isToday = ds === today;
            return (
              <div
                key={ds}
                className={`rounded-xl border p-2 min-h-[120px] transition-all ${
                  isToday
                    ? "bg-[#C9A96E]/[0.05] border-[#C9A96E]/20"
                    : "bg-white/[0.01] border-white/[0.04]"
                }`}
                data-testid={`week-day-${ds}`}
              >
                <div className="flex items-center justify-between mb-2 px-0.5">
                  <p className={`text-[11px] font-bold uppercase tracking-wider ${isToday ? "text-[#C9A96E]" : "text-white/25"}`}>
                    {d.toLocaleDateString("de-DE", { weekday: "short" })}
                  </p>
                  <p className={`text-[11px] font-mono ${isToday ? "text-[#C9A96E]/60" : "text-white/15"}`}>
                    {d.getDate()}
                  </p>
                </div>
                {di.length === 0
                  ? <p className="text-white/[0.06] text-[10px] text-center py-4">—</p>
                  : <div className="space-y-0.5">{di.map(i => <WeekItem key={i.id} item={i} onDone={markDone} onReopen={reopen} onOpen={openDeal} />)}</div>
                }
              </div>
            );
          })}
        </div>
      )}

      {/* ═══ MONTH VIEW ═══ */}
      {view === "month" && (
        <div data-testid="month-view">
          <MonthGrid
            items={items}
            date={selectedDate}
            onDayClick={(d) => { setSelectedDate(d); setView("day"); }}
            onItemClick={openDeal}
          />
        </div>
      )}

      {/* ── Deal Loading Overlay ── */}
      {loadingDeal && (
        <div className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#C9A96E]" />
        </div>
      )}

      {/* ── SlideOverPanel ── */}
      {selectedDeal && (
        <>
          <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm lg:bg-transparent lg:backdrop-blur-none" onClick={() => setSelectedDeal(null)} />
          <SlideOverPanel
            deal={selectedDeal}
            onClose={() => setSelectedDeal(null)}
            token={token}
            onUpdate={handleDealUpdate}
          />
        </>
      )}
    </div>
  );
}
